// src/context/AuthContext.tsx
import React, { createContext, useContext, ReactNode, useState, useEffect, useCallback } from 'react';
import { getAuthToken } from '../utils/auth';
import { getOrganizerProfile } from '../api';
import { OrganizerProfile } from '../types/Common';

interface AuthContextType {
  isAuthenticated: boolean;
  /** True while the first profile fetch after a token is in progress */
  isAuthLoading: boolean;
  user: OrganizerProfile | null;
  login: () => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>(null!);

/** Fired after login/logout so AuthProvider can re-fetch without a full page reload. */
export const AUTH_CHANGED_EVENT = 'eventox-auth-changed';

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(() => !!getAuthToken());
  const [user, setUser] = useState<OrganizerProfile | null>(null);

  // Fetch user profile from API
  const fetchUserProfile = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      setUser(null);
      setIsAuthenticated(false);
      setIsAuthLoading(false);
      return;
    }

    setIsAuthLoading(true);
    try {
      const profileData = await getOrganizerProfile();
      setUser(profileData);
      setIsAuthenticated(true);
      // Keep Ask Ev / other screens in sync when AuthContext finally loads
      try {
        localStorage.setItem('organizerData', JSON.stringify(profileData));
      } catch {
        // ignore quota / private mode
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUserProfile();

    const onAuthChanged = () => {
      fetchUserProfile();
    };
    window.addEventListener(AUTH_CHANGED_EVENT, onAuthChanged);
    return () => window.removeEventListener(AUTH_CHANGED_EVENT, onAuthChanged);
  }, [fetchUserProfile]);

  const login = async () => {
    await fetchUserProfile();
  };

  const logout = () => {
    setIsAuthenticated(false);
    setUser(null);
    setIsAuthLoading(false);
  };

  const refreshUser = async () => {
    await fetchUserProfile();
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, isAuthLoading, user, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
